import type { Artist, ArtistId } from 'types/artist'
import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import {
  ARTIST_IMAGE_UPDATE,
  ARTIST_IMAGE_UPDATE_FAIL,
  ARTIST_LOAD,
  ARTIST_LOAD_FAIL,
  ARTIST_LOAD_SUCCESS,
  ARTIST_SHOWCASES_LOAD,
  ARTIST_SHOWCASES_LOAD_FAIL,
  ARTIST_SHOWCASES_LOAD_SUCCESS,
  ARTIST_TRACKS_LOAD,
  ARTIST_TRACKS_LOAD_FAIL,
  ARTIST_TRACKS_LOAD_SUCCESS,
  ARTIST_UPDATE,
  ARTIST_UPDATE_FAIL,
  ARTIST_UPDATE_SUCCESS,
  ARTISTS_LOAD,
  ARTISTS_LOAD_FAIL,
  ARTISTS_LOAD_NEXT,
  ARTISTS_LOAD_NEXT_FAIL,
  ARTISTS_LOAD_NEXT_SUCCESS,
  ARTISTS_LOAD_RELATED,
  ARTISTS_LOAD_RELATED_FAIL,
  ARTISTS_LOAD_RELATED_SUCCESS,
  ARTISTS_LOAD_SUCCESS,
  DATA_LOAD,
} from '@disco/constants/ActionTypes'
import { Track } from '@disco/types/types/track'
/**
 * Loads artists based on search query and optional filter.
 * @param query - Search query to find artists
 * @param filter - Optional filter to apply to the search
 * @returns Thunk that loads artists and handles success/error states
 */
export function loadArtists(query: string, filter?: string): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTISTS_LOAD,
    })

    try {
      const response = await api.getArtists(query, filter)
      dispatch({
        type: ARTISTS_LOAD_SUCCESS,
        response,
      })
    } catch (error) {
      dispatch({
        type: ARTISTS_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads the next page of artists using pagination URL.
 * @param url - Pagination URL for the next page of artists
 * @returns Thunk that loads next page of artists
 */
export function loadNextArtists(url: string): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTISTS_LOAD_NEXT,
    })

    try {
      const response = await api.getUrl(url, null, 'api.getNextArtists')
      dispatch({
        type: ARTISTS_LOAD_NEXT_SUCCESS,
        response,
      })
    } catch (error) {
      dispatch({
        type: ARTISTS_LOAD_NEXT_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads artists related to a specific artist.
 * @param artistId - ID of the artist to find related artists for
 * @returns Thunk that loads related artists
 */
export function loadRelatedArtists(artistId: ArtistId): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTISTS_LOAD_RELATED,
    })

    try {
      const response = await api.getRelatedArtists(artistId)
      dispatch({
        type: ARTISTS_LOAD_RELATED_SUCCESS,
        response,
      })
    } catch (error) {
      dispatch({
        type: ARTISTS_LOAD_RELATED_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads detailed information for a specific artist.
 * @param artistId - ID of the artist to load
 * @returns Thunk that loads artist details
 */
export function loadArtist(artistId: ArtistId): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTIST_LOAD,
      artistId,
    })

    try {
      const response = await api.getArtist(artistId)
      dispatch({
        type: ARTIST_LOAD_SUCCESS,
        artistId,
        response,
      })
    } catch (error) {
      dispatch({
        type: ARTIST_LOAD_FAIL,
        artistId,
        error,
      })
    }
  }
}
/**
 * Loads showcase playlists for a specific artist.
 * @param artistId - ID of the artist to load showcases for
 * @returns Thunk that loads artist showcases
 */
export function loadArtistShowcases(artistId: ArtistId): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTIST_SHOWCASES_LOAD,
    })

    try {
      const response = await api.getArtistShowcases(artistId)
      dispatch({
        type: DATA_LOAD,
        data: {
          playlists: response.results,
        },
      })
      dispatch({
        type: ARTIST_SHOWCASES_LOAD_SUCCESS,
        artistId,
        response,
      })
    } catch (error) {
      dispatch({
        type: ARTIST_SHOWCASES_LOAD_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads tracks for a specific artist with optional pagination.
 * @param artistId - ID of the artist to load tracks for
 * @param next - Optional pagination URL for next page of tracks
 * @returns Thunk that loads artist tracks
 */
export function loadArtistTracks(artistId: ArtistId, next?: string): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTIST_TRACKS_LOAD,
      artistId,
      next,
    })

    try {
      let response

      if (next) {
        response = await api.getUrl(next, null, 'api.getArtistTracks')
      } else {
        response = await api.getArtistTracks(artistId)
      }

      dispatch({
        type: DATA_LOAD,
        data: {
          tracks: response.results,
        },
      })
      dispatch({
        type: ARTIST_TRACKS_LOAD_SUCCESS,
        artistId,
        list: response.results.map(track => track.id),
        next: response.next,
      })
    } catch (error) {
      dispatch({
        type: ARTIST_TRACKS_LOAD_FAIL,
        artistId,
        error,
      })
    }
  }
}
/**
 * Loads all tracks for a specific artist by paginating through all pages.
 * @param artistId - ID of the artist to load all tracks for
 * @returns Thunk that loads all artist tracks across multiple pages
 */
export function loadAllArtistTracks(artistId: ArtistId): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTIST_TRACKS_LOAD,
      artistId,
    })

    try {
      const results: Partial<Track>[] = []
      let response = await api.getArtistTracks(artistId)
      results.push(...response.results)

      while (response.next) {
        response = await api.getUrl(
          response.next,
          null,
          'api.getAllArtistTracks',
        )
        results.push(...response.results)
      }

      dispatch({
        type: DATA_LOAD,
        data: {
          tracks: results,
        },
      })
      dispatch({
        type: ARTIST_TRACKS_LOAD_SUCCESS,
        artistId,
        list: results.map(track => track.id),
        next: null,
      })
    } catch (error) {
      dispatch({
        type: ARTIST_TRACKS_LOAD_FAIL,
        artistId,
        error,
      })
    }
  }
}
/**
 * Updates artist information with the provided data.
 * @param artistId - ID of the artist to update
 * @param data - Partial artist data to update
 * @returns Thunk that updates artist information
 */
export function updateArtist(artistId: ArtistId, data: Partial<Artist>): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTIST_UPDATE,
    })

    try {
      const response = await api.updateArtist(artistId, data)
      dispatch({
        type: ARTIST_UPDATE_SUCCESS,
        artistId,
        response,
      })
    } catch (error) {
      dispatch({
        type: ARTIST_UPDATE_FAIL,
        artistId,
        error,
      })
      throw error
    }
  }
}
/**
 * Updates the artist's profile image.
 * @param artistId - ID of the artist to update image for
 * @param image - Image file to upload, or null to remove image
 * @returns Thunk that updates artist image; rejects with the server error when the upload is refused
 */
export function updateArtistImage(
  artistId: ArtistId,
  image: File | null | undefined,
): Thunk {
  return async dispatch => {
    dispatch({
      type: ARTIST_IMAGE_UPDATE,
    })

    try {
      const response = await api.updateArtistImage(artistId, image)
      dispatch({
        type: ARTIST_UPDATE_SUCCESS,
        artistId,
        response,
      })
      return response
    } catch (error) {
      dispatch({
        type: ARTIST_IMAGE_UPDATE_FAIL,
        artistId,
        error,
      })
      // rethrow so callers can surface the server error; swallowing it left
      // them reading `image_url` off `undefined` and reporting a generic
      // failure instead of the rejected upload (SUB-201)
      throw error
    }
  }
}
